import React, { useRef, useState, useEffect } from 'react';
import { Drawer, Button, Typography, List, Checkbox, Divider } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStateGlobalContext } from '../../../../../contexts/GlobalContext';

const { Text, Title } = Typography;

const PrescriptionPad = ({ visible, rxData, officeData, ptData, onClose }) => {
  const {
    officeSetting,
    preferredPharmacy,
    getPreferredPharmacy,
    providerDetails,
    getProviderDetails
  } = useStateGlobalContext();

  const printRef = useRef(null);
  const [showPharmacy, setShowPharmacy] = useState(true);
  const [isSigned, setIsSigned] = useState(true);

  const clinicInfo = officeData || officeSetting;

  useEffect(() => {
    if (visible && ptData?.id) {
      getPreferredPharmacy(ptData.id);
    }
    if (visible && rxData?.provider_id) {
      getProviderDetails(rxData.provider_id);
    }
  }, [visible, ptData?.id, rxData?.provider_id]);

  const pharmacy = Array.isArray(preferredPharmacy) ? preferredPharmacy[0] : preferredPharmacy;

  const handlePrint = () => {
    const clinicName = clinicInfo?.facility_name?.[0] || 'Medical Facility';
    const clinicAddress = [
      getVal(clinicInfo?.facility_address),
      getVal(clinicInfo?.city),
      getVal(clinicInfo?.state),
      getVal(clinicInfo?.zipcode)
    ].filter(Boolean).join(', ') || 'Address not available';

    const pName = `${ptData?.last_name || ''}, ${ptData?.first_name || ''} ${ptData?.middle_name || ''}`.trim();
    const pDOB = ptData?.date_of_birth ? dayjs(ptData.date_of_birth).format('MM/DD/YYYY') : 'N/A';
    const pID = ptData?.mrn_number || ptData?.id || 'N/A';
    const pPhone = ptData?.patient_phone || ptData?.phone || 'N/A';
    const pAddress = [
      ptData?.current_address_line_1,
      ptData?.current_address_line_2,
      ptData?.current_city,
      ptData?.current_state,
      ptData?.current_zipcode
    ].filter(Boolean).join(', ') || 'N/A';

    const rxItemsHtml = (rxData?.prescriptions || []).map((item, idx) => `
      <div style="margin-bottom: 25px; padding-bottom: 15px; border-bottom: 1px dashed #ccc; page-break-inside: avoid;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
          <strong style="font-size: 18px;">Rx ${idx + 1}: ${item.searched_medicine?.toUpperCase() || 'N/A'}</strong>
          <span style="font-weight: bold;">Qty: ${item.dispense_quantity || 1}</span>
        </div>
        <p style="font-style: italic; font-weight: bold; margin: 8px 0;">Instructions: ${item.instructions}</p>
        <p style="font-style: italic; font-weight: bold; margin: 8px 0;">Dispense as Written: ${item.dispense_as_written ? 'Yes' : 'No'}</p>
        <div style="font-size: 12px; color: #666; display: flex; gap: 20px;">
          <span>Refills: ${item.refills || 0}</span>
          <span>Date: ${item.start_date ? dayjs(item.start_date).format('MM/DD/YYYY') : ''} - ${item.end_date ? dayjs(item.end_date).format('MM/DD/YYYY') : ''}</span>
        </div>
        ${item.associated_diagnosis ? `<div style="font-size: 11px; margin-top: 5px;">Diagnosis: ${item.associated_diagnosis}</div>` : ''}
      </div>
    `).join('');

    const pharmacyHtml = (showPharmacy && pharmacy) ? `
      <div style="padding-top: 12px; border-top: 1px solid #eee; text-align: right; width: 100%;">
        <div style="color: #6b7280; font-size: 8px; text-transform: uppercase; font-weight: bold; margin-bottom: 4px;">Preferred Pharmacy</div>
        <div style="font-family: sans-serif; font-size: 11px;">
          <div style="font-weight: bold; color: #111;">${pharmacy.name || ''}</div>
          <div style="color: #4b5563; font-size: 10px;">${pharmacy.address || ''}</div>
          <div style="font-size: 10px; color: #4b5563; margin-top: 2px;">
            ${pharmacy.phone ? `<span>T: ${pharmacy.phone}</span>` : ''}
            ${pharmacy.fax ? `<span style="margin-left: 8px;">F: ${pharmacy.fax}</span>` : ''}
          </div>
        </div>
      </div>
    ` : '';

    const providerName = providerDetails?.firstname ? `${providerDetails.firstname} ${providerDetails.lastname}` : (rxData?.added_by || 'Physician');

    const printHtml = `
      <html>
        <head>
          <title>Prescription - ${pName}</title>
          <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Alex+Brush&display=swap">
          <style>
            body { font-family: 'Times New Roman', serif; padding: 30px; color: black; background: white; line-height: 1.4; }
            .header { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 30px; align-items: stretch; }
            .box { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #fff; flex: 1; display: flex; flex-direction: column; }
            .box-title { color: #6b7280; font-size: 9px; text-transform: uppercase; font-weight: bold; letter-spacing: 0.05em; margin-bottom: 8px; border-bottom: 1px solid #f3f4f6; padding-bottom: 4px; }
            .clinic-info { max-width: 55%; }
            .patient-info { max-width: 40%; text-align: right; }
            .rx-title { font-size: 20px; font-weight: bold; margin-bottom: 12px; text-transform: uppercase; border-bottom: 2px solid #000; padding-bottom: 5px; }
            .signature-section { margin-top: 50px; padding-top: 20px; border-top: 2px solid #000; display: flex; justify-content: space-between; }
            @media print {
              body { padding: 0; }
              @page { margin: 12mm; }
              .box { border-color: #000; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="box clinic-info">
              <div class="box-title">Facility Information</div>
              <h1 style="margin: 0 0 10px 0; font-size: 20px; color: #000;">${clinicName}</h1>
              <div style="font-family: sans-serif; font-size: 11px; color: #374151; display: flex; flex-direction: column; gap: 4px;">
                <div style="display: flex;"><span style="font-weight: bold; width: 60px; font-size: 9px; text-transform: uppercase; color: #6b7280;">Address:</span> <span style="flex:1;">${clinicAddress}</span></div>
                <div style="display: flex;"><span style="font-weight: bold; width: 60px; font-size: 9px; text-transform: uppercase; color: #6b7280;">Phone:</span> <span style="flex:1;">${getVal(clinicInfo?.office_phone) || 'N/A'}</span></div>
                <div style="display: flex;"><span style="font-weight: bold; width: 60px; font-size: 9px; text-transform: uppercase; color: #6b7280;">Fax:</span> <span style="flex:1;">${getVal(clinicInfo?.office_fax) || 'N/A'}</span></div>
                <div style="display: flex;"><span style="font-weight: bold; width: 60px; font-size: 9px; text-transform: uppercase; color: #6b7280;">Email:</span> <span style="flex:1;">${getVal(clinicInfo?.buisness_email) || 'N/A'}</span></div>
                ${['additional_basic_data_1', 'additional_basic_data_2', 'additional_basic_data_3'].map(key => {
      const field = clinicInfo?.[key];
      if (!isVisible(field)) return '';
      return `<div style="display: flex;"><span style="font-weight: bold; width: 60px; font-size: 9px; text-transform: uppercase; color: #6b7280;">${field?.title || 'Data'}:</span> <span style="flex:1;">${getVal(field)}</span></div>`;
    }).join('')}
              </div>
            </div>
            
            <div class="box patient-info">
              <div class="box-title">Patient Information</div>
              <div style="font-family: sans-serif; font-size: 14px; font-weight: bold; color: #111; margin-bottom: 6px;">${pName}</div>
              <div style="font-family: sans-serif; font-size: 11px; color: #374151; margin-bottom: 10px; display: flex; flex-direction: column; gap: 2px;">
                <div><span style="font-weight: bold; color: #6b7280; text-transform: uppercase; font-size: 8px;">DOB:</span> ${pDOB} | <span style="font-weight: bold; color: #6b7280; text-transform: uppercase; font-size: 8px;">ID:</span> ${pID}</div>
                <div>${pAddress}</div>
                <div style="font-weight: bold; margin-top: 2px;">${pPhone}</div>
              </div>
              <div style="flex-grow: 1;"></div>
              ${pharmacyHtml}
            </div>
          </div>

          <div class="rx-title">Prescription Details</div>
          <div style="margin-bottom: 10px; font-size: 12px; font-family: monospace;">Rx ID: ${rxData?.prescription_code || 'N/A'}</div>
          <div style="margin-bottom: 25px; text-align: right; font-weight: bold;">Date Issued: ${rxData?.date_time ? dayjs(rxData.date_time).format('MM/DD/YYYY') : ''}</div>

          <div class="medications">
            ${rxItemsHtml}
          </div>

          <div class="signature-section">
            <div style="flex: 1; max-width: 55%; border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #fff;">
              <div style="color: #6b7280; font-size: 9px; text-transform: uppercase; font-weight: bold; letter-spacing: 0.05em; margin-bottom: 8px; border-bottom: 1px solid #f3f4f6; padding-bottom: 4px;">Provider Information</div>
              ${providerDetails ? `
                <div style="font-family: sans-serif; color: #374151; font-size: 11px;">
                  <div style="font-weight: bold; font-size: 14px; color: #111827; margin-bottom: 8px;">
                    ${providerDetails.firstname} ${providerDetails.personel_settings_info?.[0]?.middle_name ? providerDetails.personel_settings_info[0].middle_name + ' ' : ''}${providerDetails.lastname}
                  </div>
                  
                  <div style="display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px;">
                    ${providerDetails.personel_settings_info?.[0]?.contact?.phone_number ? `
                      <div style="display: flex; align-items: center;">
                        <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 9px; text-transform: uppercase;">Phone:</span>
                        <span>${providerDetails.personel_settings_info[0].contact.phone_number}</span>
                      </div>
                    ` : ''}
                    ${providerDetails.personel_settings_info?.[0]?.contact?.work_phone ? `
                      <div style="display: flex; align-items: center;">
                        <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 9px; text-transform: uppercase;">Work Phone:</span>
                        <span>${providerDetails.personel_settings_info[0].contact.work_phone}</span>
                      </div>
                    ` : ''}
                    ${providerDetails.personel_settings_info?.[0]?.contact?.fax_number ? `
                      <div style="display: flex; align-items: center;">
                        <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 9px; text-transform: uppercase;">Fax:</span>
                        <span>${providerDetails.personel_settings_info[0].contact.fax_number}</span>
                      </div>
                    ` : ''}
                  </div>

                  ${providerDetails.medical_identifer_info?.[0] ? `
                    <div style="display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: #4b5563;">
                      ${providerDetails.medical_identifer_info[0].primary_speciality?.[1] === true && providerDetails.medical_identifer_info[0].primary_speciality?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Primary Spec:</span>
                          <span>${providerDetails.medical_identifer_info[0].primary_speciality[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].secondary_speciality?.[1] === true && providerDetails.medical_identifer_info[0].secondary_speciality?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Secondary Spec:</span>
                          <span>${providerDetails.medical_identifer_info[0].secondary_speciality[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].medical_license?.[1] === true && providerDetails.medical_identifer_info[0].medical_license?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Medical Lic:</span>
                          <span>${providerDetails.medical_identifer_info[0].medical_license[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].state_license?.[1] === true && providerDetails.medical_identifer_info[0].state_license?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">State Lic:</span>
                          <span>${providerDetails.medical_identifer_info[0].state_license[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].provisional_registration?.[1] === true && providerDetails.medical_identifer_info[0].provisional_registration?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Provisional:</span>
                          <span>${providerDetails.medical_identifer_info[0].provisional_registration[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].provider_tax_id?.[1] === true && providerDetails.medical_identifer_info[0].provider_tax_id?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Tax ID:</span>
                          <span>${providerDetails.medical_identifer_info[0].provider_tax_id[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].taxonomy_code?.[1] === true && providerDetails.medical_identifer_info[0].taxonomy_code?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Taxonomy:</span>
                          <span>${providerDetails.medical_identifer_info[0].taxonomy_code[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].dea_number?.[1] === true && providerDetails.medical_identifer_info[0].dea_number?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">DEA:</span>
                          <span>${providerDetails.medical_identifer_info[0].dea_number[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].additional_data_1?.[1] === true && providerDetails.medical_identifer_info[0].additional_data_1?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Data I:</span>
                          <span>${providerDetails.medical_identifer_info[0].additional_data_1[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].additional_data_2?.[1] === true && providerDetails.medical_identifer_info[0].additional_data_2?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Data II:</span>
                          <span>${providerDetails.medical_identifer_info[0].additional_data_2[0]}</span>
                        </div>
                      ` : ''}
                      ${providerDetails.medical_identifer_info[0].additional_data_3?.[1] === true && providerDetails.medical_identifer_info[0].additional_data_3?.[0] ? `
                        <div style="display: flex; align-items: center;">
                          <span style="font-weight: bold; color: #6b7280; width: 100px; font-size: 8px; text-transform: uppercase;">Data III:</span>
                          <span>${providerDetails.medical_identifer_info[0].additional_data_3[0]}</span>
                        </div>
                      ` : ''}
                    </div>
                  ` : ''}
                </div>
              ` : ''}
              <div style="font-size: 9px; color: #9ca3af; font-style: italic; margin-top: 12px;">
                This prescription was generated electronically. Contact the medical facility for verification.
              </div>
            </div>
            <div style="min-width: 200px; text-align: right;">
              ${isSigned ? `
                <div style="font-family: 'Alex Brush', cursive; font-size: 28px; color: #1e40af; margin-bottom: 5px;">${providerName}</div>
                <div style="border-top: 1px solid #000; padding-top: 5px;">
                  <div style="font-weight: bold; font-size: 12px; text-transform: uppercase;">Physician Signature</div>
                  <div style="font-size: 10px;">Electronically signed</div>
                </div>
              ` : `
                <div style="border-top: 1px solid #000; padding-top: 40px; margin-top: 8px; min-height: 80px;">
                  <div style="font-weight: bold; font-size: 12px; text-transform: uppercase;">Physician Signature</div>
                </div>
              `}
            </div>
          </div>
        </body>
      </html>
    `;

    // Use hidden iframe for printing
    let iframe = document.getElementById('prescription-print-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'prescription-print-iframe';
      iframe.style.position = 'absolute';
      iframe.style.top = '-9999px';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
    }

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    // Small timeout to ensure content is ready
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 500);
  };

  // Helper to extract value from officeSetting array structure [value, isVisible]
  const getVal = (field) => (Array.isArray(field) ? field[0] : field);
  const isVisible = (field) => (Array.isArray(field) ? field[1] === true : true);

  const patientName = `${ptData?.last_name || ''}, ${ptData?.first_name || ''} ${ptData?.middle_name || ''}`.trim();
  const patientAddress = [
    ptData?.current_address_line_1,
    ptData?.current_address_line_2,
    ptData?.current_city,
    ptData?.current_state,
    ptData?.current_zipcode
  ].filter(Boolean).join(', ');

  return (
    <Drawer
      title="Prescription Print View"
      placement="right"
      width="900px"
      onClose={onClose}
      open={visible}
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Checkbox
            checked={showPharmacy}
            onChange={e => setShowPharmacy(e.target.checked)}
          >
            Show Pharmacy
          </Checkbox>
          <Checkbox
            checked={isSigned}
            onChange={e => setIsSigned(e.target.checked)}
          >
            Signed
          </Checkbox>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            style={{ backgroundColor: '#0ea5e9', borderColor: '#0ea5e9' }}
          >
            Print Now
          </Button>
        </div>
      }
    >
      <div ref={printRef} className="bg-white">
        <div className="p-8 text-black font-serif page-break-avoid" style={{ color: 'black' }}>
          {/* TOP SECTION: CLINIC (LEFT) AND PATIENT/PHARMACY (RIGHT) */}
          <div className="flex justify-between pb-6 mb-4 page-break-avoid" style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '24px', marginBottom: '16px', gap: '20px', alignItems: 'stretch' }}>
            {/* LEFT: FACILITY INFO BOX */}
            <div style={{ maxWidth: '58%', flex: 1, border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', backgroundColor: '#f9fafb' }}>
              <Text style={{ display: 'block', color: '#9ca3af', textTransform: 'uppercase', fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.1em', marginBottom: '12px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                Facility Information
              </Text>
              {clinicInfo && (
                <div className="text-left font-sans text-black">
                  <Title level={4} style={{ margin: 0, color: 'black', fontFamily: 'serif', fontWeight: 'bold', letterSpacing: '-0.025em', marginBottom: '12px' }}>
                    {getVal(clinicInfo.facility_name)}
                  </Title>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px' }}>
                    {isVisible(clinicInfo.facility_address) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ fontWeight: 'bold', color: '#6b7280', width: '90px', flexShrink: 0, textTransform: 'uppercase', fontSize: '8px' }}>Address:</div>
                        <div style={{ flex: 1, color: '#374151' }}>
                          {[
                            getVal(clinicInfo.facility_address),
                            getVal(clinicInfo.city),
                            getVal(clinicInfo.state),
                            getVal(clinicInfo.zipcode)
                          ].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}
                    {isVisible(clinicInfo.office_phone) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ fontWeight: 'bold', color: '#6b7280', width: '90px', flexShrink: 0, textTransform: 'uppercase', fontSize: '8px' }}>Phone:</div>
                        <div style={{ flex: 1, color: '#374151' }}>{getVal(clinicInfo.office_phone)}</div>
                      </div>
                    )}
                    {isVisible(clinicInfo.buisness_email) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <div style={{ fontWeight: 'bold', color: '#6b7280', width: '90px', flexShrink: 0, textTransform: 'uppercase', fontSize: '8px' }}>Email:</div>
                        <div style={{ flex: 1, color: '#374151' }}>{getVal(clinicInfo.buisness_email)}</div>
                      </div>
                    )}
                    {['additional_basic_data_1', 'additional_basic_data_2', 'additional_basic_data_3'].map((key) => {
                      const field = clinicInfo[key];
                      if (!isVisible(field)) return null;
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <div style={{ fontWeight: 'bold', color: '#6b7280', width: '90px', flexShrink: 0, textTransform: 'uppercase', fontSize: '8px' }}>{field?.title || 'Data'}:</div>
                          <div style={{ flex: 1, color: '#374151' }}>{getVal(field)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: PATIENT INFO BOX */}
            <div style={{ maxWidth: '40%', flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', backgroundColor: '#f9fafb', textAlign: 'right' }}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ display: 'block', color: '#9ca3af', textTransform: 'uppercase', fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.1em', marginBottom: '12px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                  Patient Information
                </Text>
                <div style={{ fontFamily: 'sans-serif', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '4px', color: 'black' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '15px', textTransform: 'uppercase', lineHeight: 1.25, marginBottom: '6px', color: '#111827' }}>
                    {patientName}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', fontSize: '10px' }}>
                    <span><span style={{ fontWeight: 'bold', color: '#9ca3af', textTransform: 'uppercase', marginRight: '4px', fontSize: '8px' }}>DOB:</span>{ptData?.date_of_birth ? dayjs(ptData.date_of_birth).format('MM/DD/YYYY') : 'N/A'}</span>
                    <span><span style={{ fontWeight: 'bold', color: '#9ca3af', textTransform: 'uppercase', marginRight: '4px', fontSize: '8px' }}>ID:</span>{ptData?.mrn_number || ptData?.id}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#4b5563', lineHeight: 1.25, marginTop: '4px' }}>
                    {patientAddress || 'N/A'}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#111827' }}>
                    {ptData?.patient_phone || ptData?.phone || 'N/A'}
                  </div>
                </div>
              </div>

              {/* PHARMACY INFO */}
              {showPharmacy && pharmacy && (
                <div style={{ paddingTop: '12px', borderTop: '1px solid #e5e7eb', marginTop: 'auto' }}>
                  <Text style={{ display: 'block', color: '#9ca3af', textTransform: 'uppercase', fontSize: '8px', fontWeight: 'bold', letterSpacing: '0.1em', marginBottom: '6px' }}>
                    Preferred Pharmacy
                  </Text>
                  <div style={{ fontSize: '11px', fontFamily: 'sans-serif' }}>
                    <p style={{ margin: 0, fontWeight: 'bold', color: '#111827' }}>{pharmacy.name || 'No Pharmacy Selected'}</p>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '10px' }}>{pharmacy.address || ''}</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '4px', color: '#6b7280', fontSize: '10px' }}>
                      {pharmacy.phone && <span><span style={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '8px', marginRight: '4px' }}>T:</span>{pharmacy.phone}</span>}
                      {pharmacy.fax && <span><span style={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '8px', marginRight: '4px' }}>F:</span>{pharmacy.fax}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RX HEADER */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', borderBottom: '1px solid #d1d5db', paddingBottom: '8px' }}>
            <div>
              <Title level={4} style={{ margin: 0, textTransform: 'uppercase' }}>
                Prescription Details
              </Title>
              <Text type="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                Rx ID: {rxData?.prescription_code}
              </Text>
            </div>
            <Text strong>
              Date Issued:{' '}
              {rxData?.date_time
                ? dayjs(rxData.date_time).format('MM/DD/YYYY')
                : ''}
            </Text>
          </div>

          {/* MEDICATIONS */}
          <List
            dataSource={rxData?.prescriptions || []}
            renderItem={(item) => (
              <div
                style={{ marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px dashed #d1d5db' }}
                className="page-break-avoid"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '20px' }}>
                    Rx: {item.searched_medicine?.toUpperCase()}
                  </Text>
                  <Text strong>
                    Quantity: {item.dispense_quantity || 1}
                  </Text>
                </div>

                <p style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                  Instructions: {item.instructions}
                </p>
                <p>Dispense as Written: {item.dispense_as_written ? 'Yes' : 'No'}</p>

                <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>
                  <span style={{ marginRight: '24px' }}>
                    Refills: {item.refills || 0}
                  </span>
                  <span>
                    Duration:{' '}
                    {item.start_date
                      ? dayjs(item.start_date).format('MM/DD/YYYY')
                      : ''}{' '}
                    -{' '}
                    {item.end_date
                      ? dayjs(item.end_date).format('MM/DD/YYYY')
                      : ''}
                  </span>
                </div>

                {item.associated_diagnosis && (
                  <p style={{ marginTop: '8px', fontSize: '12px' }}>
                    Diagnosis: {item.associated_diagnosis}
                  </p>
                )}

              </div>
            )}
          />

          {/* SIGNATURE AND PROVIDER DETAILS */}
          <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '2px solid #d1d5db', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }} className="page-break-avoid">
            {/* LEFT: PROVIDER DETAILS BOX */}
            <div style={{ flex: 1, maxWidth: '55%', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', backgroundColor: '#f9fafb' }}>
              <Text style={{ display: 'block', color: '#9ca3af', textTransform: 'uppercase', fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.1em', marginBottom: '12px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                Provider Information
              </Text>

              {providerDetails && (
                <div style={{ fontFamily: 'sans-serif', fontSize: '11px', color: '#374151' }}>
                  {/* Provider Name */}
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#111827', marginBottom: '8px' }}>
                    {providerDetails.firstname} {providerDetails.personel_settings_info?.[0]?.middle_name && `${providerDetails.personel_settings_info[0].middle_name} `}{providerDetails.lastname}
                  </div>

                  {/* Contact Information */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                    {providerDetails.personel_settings_info?.[0]?.contact?.phone_number && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '9px', textTransform: 'uppercase' }}>Phone:</span>
                        <span>{providerDetails.personel_settings_info[0].contact.phone_number}</span>
                      </div>
                    )}
                    {providerDetails.personel_settings_info?.[0]?.contact?.work_phone && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '9px', textTransform: 'uppercase' }}>Work Phone:</span>
                        <span>{providerDetails.personel_settings_info[0].contact.work_phone}</span>
                      </div>
                    )}
                    {providerDetails.personel_settings_info?.[0]?.contact?.fax_number && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '9px', textTransform: 'uppercase' }}>Fax:</span>
                        <span>{providerDetails.personel_settings_info[0].contact.fax_number}</span>
                      </div>
                    )}
                  </div>

                  {/* Medical Identifiers */}
                  {providerDetails.medical_identifer_info?.[0] && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '11px', color: '#4b5563' }}>
                      {providerDetails.medical_identifer_info[0].primary_speciality?.[1] === true && providerDetails.medical_identifer_info[0].primary_speciality?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Primary Spec:</span>
                          <span>{providerDetails.medical_identifer_info[0].primary_speciality[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].secondary_speciality?.[1] === true && providerDetails.medical_identifer_info[0].secondary_speciality?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Secondary Spec:</span>
                          <span>{providerDetails.medical_identifer_info[0].secondary_speciality[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].medical_license?.[1] === true && providerDetails.medical_identifer_info[0].medical_license?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Medical Lic:</span>
                          <span>{providerDetails.medical_identifer_info[0].medical_license[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].state_license?.[1] === true && providerDetails.medical_identifer_info[0].state_license?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>State Lic:</span>
                          <span>{providerDetails.medical_identifer_info[0].state_license[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].provisional_registration?.[1] === true && providerDetails.medical_identifer_info[0].provisional_registration?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Provisional:</span>
                          <span>{providerDetails.medical_identifer_info[0].provisional_registration[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].provider_tax_id?.[1] === true && providerDetails.medical_identifer_info[0].provider_tax_id?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Tax ID:</span>
                          <span>{providerDetails.medical_identifer_info[0].provider_tax_id[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].taxonomy_code?.[1] === true && providerDetails.medical_identifer_info[0].taxonomy_code?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Taxonomy:</span>
                          <span>{providerDetails.medical_identifer_info[0].taxonomy_code[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].dea_number?.[1] === true && providerDetails.medical_identifer_info[0].dea_number?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>DEA:</span>
                          <span>{providerDetails.medical_identifer_info[0].dea_number[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].additional_data_1?.[1] === true && providerDetails.medical_identifer_info[0].additional_data_1?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Data I:</span>
                          <span>{providerDetails.medical_identifer_info[0].additional_data_1[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].additional_data_2?.[1] === true && providerDetails.medical_identifer_info[0].additional_data_2?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Data II:</span>
                          <span>{providerDetails.medical_identifer_info[0].additional_data_2[0]}</span>
                        </div>
                      )}
                      {providerDetails.medical_identifer_info[0].additional_data_3?.[1] === true && providerDetails.medical_identifer_info[0].additional_data_3?.[0] && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: '#6b7280', width: '100px', fontSize: '8px', textTransform: 'uppercase' }}>Data III:</span>
                          <span>{providerDetails.medical_identifer_info[0].additional_data_3[0]}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <p style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic', marginTop: '12px', marginBottom: 0 }}>
                This prescription was generated electronically. Contact the medical facility for verification.
              </p>
            </div>

            {/* RIGHT: SIGNATURE SECTION */}
            <div style={{ textAlign: 'right', minWidth: '200px' }}>
              {isSigned ? (
                <>
                  <Text style={{ fontFamily: "'Alex Brush', cursive", fontSize: '28px', color: '#1e40af' }}>
                    {providerDetails?.firstname ? `${providerDetails.firstname} ${providerDetails.lastname}` : (rxData?.added_by || 'Physician')}
                  </Text>
                  <div style={{ borderTop: '1px solid black', paddingTop: '4px', marginTop: '8px' }}>
                    <Text strong style={{ textTransform: 'uppercase', fontSize: '12px', display: 'block' }}>
                      Physician Signature
                    </Text>
                    <Text style={{ fontSize: '10px' }}>
                      Electronically signed
                    </Text>
                  </div>
                </>
              ) : (
                <div style={{ borderTop: '1px solid black', paddingTop: '40px', marginTop: '8px', minHeight: '80px' }}>
                  <Text strong style={{ textTransform: 'uppercase', fontSize: '12px', display: 'block' }}>
                    Physician Signature
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default PrescriptionPad;
